import { useMemo } from 'react';
import { useParams } from 'react-router-dom';

import Modal, { ModalProps, ModalVariant } from 'src/components/organisms/Modal';
import { useSendReport } from 'src/features/incomeVerification/hooks';
import { getIncomeDocumentsFromSession } from 'src/features/incomeVerification/utils';

type SendReportModalProps = {
  onConfirm?: () => void;
  selectedEntryId: string;
  showSendReportModal: ModalProps['showModal'];
  setShowSendReportModal: ModalProps['setShowModal'];
  hasOverrideSelections?: boolean;
  lastCalculationId?: string;
  onCalculationOverride?: (
    entryId: string | undefined,
    calculationId: string | undefined,
    note?: string
  ) => Promise<boolean>;
  sessionKey?: string;
  isCalcDispute?: boolean;
  note?: string;
};

const SendReportModal = ({
  onConfirm,
  selectedEntryId,
  showSendReportModal,
  setShowSendReportModal,
  hasOverrideSelections,
  lastCalculationId,
  onCalculationOverride,
  sessionKey,
  isCalcDispute,
  note
}: SendReportModalProps): JSX.Element => {
  const { onSendReport } = useSendReport();
  const { incomeVerificationReviewId } = useParams();

  const sendReportModalProps = useMemo(
    () => ({
      title: 'Send Report',
      labelButtonConfirm: 'Send Report',
      text: 'Are you sure you want to send this report?',
      showModal: showSendReportModal,
      setShowModal: setShowSendReportModal,
      onConfirm: async () => {
        setShowSendReportModal?.(false);

        if (isCalcDispute) {
          // Calc dispute: persist override via dedicated endpoint, no review to complete
          if (lastCalculationId && onCalculationOverride) {
            const overrideSuccess = await onCalculationOverride(
              selectedEntryId,
              lastCalculationId,
              note
            );
            if (!overrideSuccess) return;
          }
          onConfirm?.();
          return;
        }

        // Normal review flow: optionally persist override, then complete review
        if (hasOverrideSelections && lastCalculationId && onCalculationOverride) {
          const overrideSuccess = await onCalculationOverride(selectedEntryId, lastCalculationId);
          if (!overrideSuccess) return;
        }

        const documents = getIncomeDocumentsFromSession(
          sessionKey ?? (incomeVerificationReviewId as string)
        );

        const success = await onSendReport(selectedEntryId, documents);
        if (success && onConfirm) {
          await onConfirm();
        }
      }
    }),
    [
      showSendReportModal,
      setShowSendReportModal,
      incomeVerificationReviewId,
      sessionKey,
      onSendReport,
      selectedEntryId,
      onConfirm,
      hasOverrideSelections,
      lastCalculationId,
      onCalculationOverride,
      isCalcDispute,
      note
    ]
  );

  return <Modal {...sendReportModalProps} variant={ModalVariant.none} labelButtonCancel="Cancel" />;
};

export default SendReportModal;
